import { useState, useEffect, useMemo, useCallback } from 'react';
import { 
    collectionGroup, 
    query, 
    where, 
    onSnapshot, 
    doc,
    getDoc,
    orderBy
} from 'firebase/firestore';

import { db } from '../../../../../firebase/config';
import { useAuth } from '../../../../hooks/useAuth'; 
import { deleteEndosoArtFull } from '../../../../../firebase/firebaseUtils';
import { useTableState } from '../../hooks/useTableState';

/**
 * useAumentosArtTable - Hook de negocio unificado para el Tablero de Aumentos de ART.
 */
export const useAumentosArtTable = () => {
    const { user, profile } = useAuth();
    
    const isFullUser = true;

    // --- ESTADOS DE CONTROL DE VISTA ---
    const [view, setView] = useState('list'); // 'list' | 'create' | 'edit'
    const [step, setStep] = useState(1); // 1: Buscar Contrato | 2: Formulario

    // --- ESTADOS DE BASE DE DATOS CRUDA ---
    const [aumentos, setAumentos] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- ESTADOS DE BUSCADOR DE CONTRATOS EN CREACIÓN ---
    const [searchResults, setSearchResults] = useState([]);
    const [searchPerformed, setSearchPerformed] = useState(false);
    const [loadingSearch, setLoadingSearch] = useState(false);

    // --- ENTIDADES SELECCIONADAS ---
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [selectedContract, setSelectedContract] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [authModalConfig, setAuthModalConfig] = useState({ open: false, type: '', item: null });

    // CÁLCULO DINÁMICO DEL LÍMITE DE 6 MESES ATRÁS
    const limiteFecha = useMemo(() => {
        const date = new Date();
        date.setMonth(date.getMonth() - 6);
        return date.toISOString().split('T')[0];
    }, []);

    // 1. LISTENER DE FIRESTORE EN TIEMPO REAL CON LÍMITE TEMPORAL
    useEffect(() => {
        if (!db) return;
        
        const q = query(
            collectionGroup(db, 'aumentos_art'), 
            where('fechaCarga', '>=', limiteFecha),
            orderBy('fechaCarga', 'desc')
        );
        
        const unsubscribe = onSnapshot(q, (snap) => {
            const data = snap.docs.map(d => {
                const docData = d.data();
                const pathParts = d.ref.path.split('/');
                const cliId = pathParts[3] || docData.clienteId;

                return { 
                    id: d.id, 
                    path: d.ref.path,
                    clienteId: cliId,
                    clientId: cliId, 
                    contratoId: pathParts[5] || docData.contratoId,
                    ...docData,
                    clienteNombre: docData.clienteNombre || docData.entidadNombre || '',
                    clienteApellido: docData.clienteApellido || '',
                    clienteCuit: docData.clienteCuit || docData.entidadCuit || '',
                    clienteDni: docData.clienteDni || '',
                    canal: docData.canal || docData.tipoCanal || docData.canalOrigen || '-',
                    canalNombre: docData.canalNombre || docData.nombreCanal || docData.canalNombreId || '-',
                    subcanalNombre: docData.subcanalNombre || docData.subcanalAsociado || docData.subcanalNombreId || '-'
                };
            });
            setAumentos(data);
            setLoading(false);
        }, (error) => {
            console.error("Error cargando aumentos ART:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [limiteFecha]);

    // 2. MAPEO SÍNCRONO CON SANEAR DE "S/D"
    const itemsTable = useMemo(() => {
        return aumentos.map(item => {
            const nombre = (item.clienteNombre || item.entidadNombre || '').trim();
            const apellido = (item.clienteApellido || '').trim();

            // Saneamiento: Si el apellido es S/D, SD, S/A o Sin Datos, no se concatena (personas jurídicas)
            const esValidoApellido = apellido && !['S/D', 'SD', 'S/A', 'SIN DATOS'].includes(apellido.toUpperCase());
            const fullName = esValidoApellido ? `${nombre} ${apellido}`.trim() : nombre;

            return {
                ...item,
                displayNombre: (fullName || 'SIN NOMBRE').toUpperCase(),
                displayDoc: item.clienteCuit || item.clienteDni || '-',
                porcentajeAumento: item.porcentajeAumento || 0,
                masaSalarial: item.masaSalarial || 0 
            };
        });
    }, [aumentos]);

    // 3. HOOK DE CONTROL SÍNCRONO DE TABLA
    const rawTableState = useTableState({
        data: itemsTable,
        initialSort: { key: 'fechaCarga', direction: 'descending' },
        initialColumns: {
            fechaCarga: true, mesAumento: true, periodoArca: true, nContrato: true,
            displayDoc: true, displayNombre: true, numeroEndoso: true, compania: true,
            capitas: true, masaSalarial: true, alicuotaActual: true, alicuotaAumentada: true,
            porcentajeAumento: true, estado: true, canal: true, canalNombre: true,
            subcanalNombre: true, responsableEndoso: true
        },
        initialFilters: {
            estado: ['Pendiente', 'Informado', 'Cotizado',] // Sello de filtro por defecto al ingresar
        },
        recordsPerPage: 100
    });

    // 4. INTERCEPTOR ESTABLE PARA NO REINICIAR PAGINACIÓN DE FORMA INVOLUNTARIA
    const baseHandleFilterChange = rawTableState.handleFilterChange;
    const handleFilterChange = useCallback((vals) => {
        baseHandleFilterChange(vals);
    }, [baseHandleFilterChange]);

    // 5. UNIFICACIÓN MEMOIZADA ESTABLE DE TABLESTATE
    const tableState = useMemo(() => ({
        ...rawTableState,
        handleFilterChange
    }), [rawTableState, handleFilterChange]);

    const contractResults = useMemo(() => {
        return searchResults.filter(r => {
            const entityType = String(r.type || r.tipo || '').toLowerCase();
            return entityType.includes('contrato') || !!r.nContrato;
        });
    }, [searchResults]);

    const handleContractSelect = useCallback(async (contract) => {
        try {
            const clientDoc = await getDoc(doc(db, 'public/data/clientes', contract.clienteId));
            if (clientDoc.exists()) {
                setSelectedEntity({ id: clientDoc.id, ...clientDoc.data() });
                setSelectedContract(contract);
                setStep(2);
            }
        } catch (e) { 
            console.error("Error al recuperar entidad de contrato:", e); 
        }
    }, []);

    const handleCreateNew = useCallback(() => {
        setEditingItem(null);
        setSelectedEntity(null);
        setSelectedContract(null);
        setSearchResults([]);
        setSearchPerformed(false);
        setView('create');
        setStep(1);
    }, []);

    const handleSuccess = useCallback(() => {
        setView('list');
        setStep(1);
        setEditingItem(null);
        setSelectedEntity(null);
        setSelectedContract(null);
        setSearchResults([]);
        setSearchPerformed(false);
    }, []);

    const handleBack = useCallback(() => {
        if (view === 'create') {
            if (step === 2) {
                setStep(1);
                setSelectedEntity(null);
                setSelectedContract(null);
            } else { 
                setView('list'); 
                setSearchResults([]); 
                setSearchPerformed(false); 
                setEditingItem(null);
            }
        } else if (view === 'edit') {
            setView('list'); 
            setEditingItem(null);
        }
    }, [view, step]);

    const handleAction = useCallback((item, type, navigate) => {
        if (type === 'EDIT') {
            const { displayNombre, displayDoc, importeDeuda, ...cleanItem } = item;
            setEditingItem(cleanItem); 
            setView('edit');
        }
        if (type === 'DELETE') { 
            setAuthModalConfig({ open: true, type: 'DELETE', item }); 
        }
        if (type === 'VIEW_QUOTE') {
            navigate('/DetalleAumentoArt', {
                state: { 
                    cotizacionId: item.cotizacionVinculadaId, 
                    entidadId: item.clienteId, 
                    clientId: item.clienteId, 
                    contratoId: item.contratoId, 
                    endosoId: item.id, 
                    nCotizacion: item.cotizacionVinculada, 
                    fromTableroAumentos: true 
                }
            });
        }
        if (type === 'GENERATE_QUOTE') {
            const tieneCotizacion = Boolean(item.cotizacionVinculadaId || item.cotizacionVinculada);
            navigate('/FormularioCotizacionArt', {
                state: { 
                    isEdit: tieneCotizacion, 
                    cotizacionId: item.cotizacionVinculadaId, 
                    fromEndoso: true, 
                    gestionTipo: 'Aumento de Tarifa', 
                    endosoVinculadoId: item.id, 
                    endosoPath: item.path, 
                    numeroEndoso: item.numeroEndoso,
                    clienteId: item.clienteId, 
                    clienteNombre: item.clienteNombre, 
                    clienteApellido: item.clienteApellido,
                    clienteCuit: item.clienteCuit, 
                    clienteDni: item.clienteDni,
                    canal: item.canal,
                    canalNombre: item.canalNombre,
                    subcanalNombre: item.subcanalNombre,
                    contratoId: item.contratoId,
                    nContrato: item.nContrato,
                    compania: item.compania, 
                    capitas: item.capitas, 
                    masaSalarial: item.masaSalarial, 
                    alicuotaActual: item.alicuotaActual,
                    ciiu: item.ciiu,
                    mesAumento: item.mesAumento,
                    periodoArca: item.periodoArca,
                    alicuotaAumentada: item.alicuotaAumentada,
                    porcentajeAumento: item.porcentajeAumento,
                }
            });
        }
    }, []);

    const handleDelete = useCallback(async () => {
        if (!authModalConfig.item) return;
        try {
            await deleteEndosoArtFull(authModalConfig.item);
            setAuthModalConfig({ open: false, type: '', item: null });
        } catch (e) { 
            console.error("Error al eliminar endoso de ART:", e); 
        }
    }, [authModalConfig.item]);

    return {
        view,
        setView,
        step,
        setStep,
        loading,
        searchResults,
        setSearchResults,
        searchPerformed,
        setSearchPerformed,
        loadingSearch,
        setLoadingSearch,
        selectedEntity,
        selectedContract,
        editingItem,
        authModalConfig,
        setAuthModalConfig,
        isFullUser,
        tableState,
        itemsTable,
        contractResults,
        handleContractSelect,
        handleBack,
        handleAction,
        handleDelete,
        handleCreateNew,
        handleSuccess
    };
};